import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

export interface ProgressZone {
  from: number;
  to: number;
  color: string;
}

@Component({
  selector: 'app-circular-progress',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './circular-progress.component.html',
  styleUrls: ['./circular-progress.component.scss']
})
export class CircularProgressComponent  implements OnChanges {
  @Input() percentage: number = 0;
  @Input() label: string = '';
  @Input() labelColor: string = '#28a745';
  @Input() progressColor: string = '#28a745';
  @Input() zones: ProgressZone[] = [];
  @Input() backgroundStrokeWidth: number = 20;
  @Input() progressStrokeWidth: number = 10;

  backgroundRadius: number = 50;
  progressRadius: number = 0;
  circumference: number = 0;
  strokeDashoffset: number = 0;

  ngOnInit(): void {
    this.calculateRadii();
  }

  ngOnChanges(): void {
    this.calculateRadii();
  }

  private calculateRadii(): void {
    const radiusDifference = (this.backgroundStrokeWidth - this.progressStrokeWidth) / 2;
    this.progressRadius = this.backgroundRadius + radiusDifference;

    this.circumference = 2 * Math.PI * this.progressRadius;
    this.updateProgress();
  }

  private updateProgress(): void {
    const validPercentage = Math.max(0, Math.min(100, this.percentage));
    const offset = this.circumference - (validPercentage / 100) * this.circumference;
    this.strokeDashoffset = offset;
  }

  getArcPath(startPercent: number, endPercent: number): string {
    const startAngle = (startPercent / 100) * 360;
    const endAngle = (endPercent / 100) * 360;
    
    if (endPercent - startPercent > 99.99) {
        return this.getArcPath(startPercent, startPercent + 99.99);
    }

    const start = this.polarToCartesian(60, 60, this.backgroundRadius, endAngle);
    const end = this.polarToCartesian(60, 60, this.backgroundRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    const d = `M ${start.x} ${start.y} A ${this.backgroundRadius} ${this.backgroundRadius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
    return d;
  }
  
  private polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }
}